import type { FC } from 'react';
import React, { useCallback } from 'react';
import {
  Modal,
  ModalInner,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import type { BaseData, LegalEntity } from '@pflegenavi/shared/api';
import { LegalEntityForm } from './LegalEntityForm';
import { useUpdateLegalEntityPhoenix } from '@pflegenavi/frontend/api-nursing-home';

export interface EditLegalEntityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  legalEntity: LegalEntity;
}

export const EditLegalEntityModal: FC<EditLegalEntityModalProps> = ({
  open,
  setOpen,
  legalEntity,
}) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  const { mutateAsync: updateLegalEntity } = useUpdateLegalEntityPhoenix({
    legalEntityId: legalEntity.id,
  });

  const handleClose = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const handleSubmitLegalEntity = useCallback(
    async (data: { id: string; base_data: BaseData }) => {
      try {
        await updateLegalEntity(data.base_data);
        enqueueSnackbar(t('legal-entities.form.success.updated'), {
          variant: 'success',
        });
        setOpen(false);
      } catch (e) {
        enqueueErrorSnackbar(
          e,
          t('legal-entities.form.error.failed-to-update')
        );
      }
    },
    [updateLegalEntity, enqueueErrorSnackbar, enqueueSnackbar, setOpen, t]
  );

  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <LegalEntityForm
          title={t('legal-entities.edit-legal-entity')}
          setOpen={setOpen}
          handleSubmitLegalEntity={handleSubmitLegalEntity}
          legalEntity={legalEntity}
        />
      </ModalInner>
    </Modal>
  );
};
